import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';

export default function AcceptInvite() {
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const [invalidLink, setInvalidLink] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const hash = window.location.hash;
    const params = new URLSearchParams(hash.replace('#', '?'));
    const type = params.get('type');
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');

    const queryParams = new URLSearchParams(window.location.search);
    const errorParam = queryParams.get('error');
    if (errorParam) {
      setInvalidLink(true);
      return;
    }

    if (accessToken && refreshToken && (type === 'invite' || type === 'recovery')) {
      supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken })
        .then(({ error }) => {
          if (error) {
            setInvalidLink(true);
          } else {
            setSessionReady(true);
          }
        });
    } else if (!accessToken) {
      supabase.auth.getSession().then(({ data }) => {
        if (!data.session) {
          setInvalidLink(true);
        } else {
          setSessionReady(true);
        }
      });
    }
  }, []);

  const validatePassword = (pwd: string): string | null => {
    if (pwd.length < 8) return 'Password must be at least 8 characters';
    if (!/[A-Z]/.test(pwd)) return 'Password must contain at least one uppercase letter';
    if (!/[0-9]/.test(pwd)) return 'Password must contain at least one number';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!fullName.trim()) {
      toast.error('Please enter your full name');
      return;
    }
    if (!phone.trim()) {
      toast.error('Please enter your phone number');
      return;
    }
    const pwdError = validatePassword(password);
    if (pwdError) {
      toast.error(pwdError);
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password,
        data: {
          full_name: fullName.trim(),
          phone: phone.trim(),
        },
      });

      if (updateError) throw updateError;

      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        // Upsert to ensure the row exists even if the trigger didn't fire
        await supabase
          .from('auth_users')
          .upsert({
            id: user.id,
            email: user.email ?? '',
            full_name: fullName.trim(),
            role: user.user_metadata?.role || 'admin',
            is_active: true,
          }, { onConflict: 'id' });
      }

      await supabase.auth.signOut();

      toast.success('Account set up successfully! Please sign in to continue.');

      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (err: any) {
      toast.error(err.message || 'Failed to set up account. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  if (invalidLink) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-golf-sand via-white to-golf-green/10 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-2xl font-display font-bold text-golf-gray mb-3">Invalid or Expired Link</h1>
          <p className="text-gray-600 mb-6">This invitation link is invalid or has expired. Please request a new invitation from an administrator.</p>
          <button
            onClick={() => navigate('/')}
            className="modern-button py-3 px-6 text-white font-medium"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-golf-sand via-white to-golf-green/10 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-golf-green/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-golf-green" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <h1 className="text-3xl font-display font-bold text-golf-gray mb-2">
            Set Up Your Account
          </h1>
          <p className="text-gray-600">
            Complete your profile to access the admin dashboard
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">
              Full Name
            </label>
            <input
              type="text"
              id="fullName"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golf-orange focus:border-transparent"
              required
              disabled={loading}
              maxLength={100}
              placeholder="John Doe"
            />
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number
            </label>
            <input
              type="tel"
              id="phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golf-orange focus:border-transparent"
              required
              disabled={loading}
              maxLength={20}
              placeholder="+234 800 000 0000"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golf-orange focus:border-transparent"
              required
              disabled={loading}
              minLength={8}
              placeholder="Min 8 characters, 1 uppercase, 1 number"
            />
            <p className="text-xs text-gray-500 mt-1">
              At least 8 characters, one uppercase letter, and one number
            </p>
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
              Confirm Password
            </label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golf-orange focus:border-transparent"
              required
              disabled={loading}
              minLength={8}
              placeholder="Re-enter your password"
            />
          </div>

          <button
            type="submit"
            className="w-full modern-button py-3 text-white font-medium disabled:opacity-50"
            disabled={loading || !sessionReady}
          >
            {loading ? 'Setting Up Account...' : 'Complete Account Setup'}
          </button>

          {!sessionReady && !invalidLink && (
            <p className="text-xs text-center text-gray-500">Verifying invitation link...</p>
          )}
        </form>
      </div>
    </div>
  );
}
